import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CertificatesService } from 'services/professional/certificates/certificates.service';
import { StepService } from 'services/professional/step/step.service';
import { MessageQueryService, skipModalClose, SmartModalService } from '@certemy/ui-components';
import { Observable } from 'rxjs';
import _size from 'lodash-es/size';
import { ComputedFrom, DateFormatPipe } from '@certemy/common';
import { RejectedHintModalComponent } from 'components/hint/rejected-hint/rejected-hint-modal/rejected-hint-modal.component';
import { REJECT_HINT_TYPES } from '../hint.constants';
import { TypeConfig } from '../hint.interface';
import { RejectStepData } from 'services/professional/certificates/certificates.interface';
import { ROLE_CODES } from 'constants/role.constants';
import { defaultSupervisorTitle, SUPERVISOR_APPROVAL_TYPE } from 'constants/log-step.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-rejected-hint',
  templateUrl: './rejected-hint.component.html',
  styleUrls: ['./rejected-hint.component.scss'],
})
export class RejectedHintComponent implements OnDestroy, OnChanges {
  @Input() id: number;
  @Input() type: REJECT_HINT_TYPES;
  @Input() showTooltip = false;
  @Input() showConfirmButton = false;
  @Input() notes: string[];
  @Input() organizationContactData = { name: '', email: '' };
  @Input() rejectStepData: RejectStepData;
  @Input() onlyIcon = false;
  @Output() modalConfirm = new EventEmitter();
  readonly REJECT_HINT_TYPES = REJECT_HINT_TYPES;
  _rejectStepData: RejectStepData = null;
  _notes: string[] = null;
  _hasDefaultNotes: boolean;

  @ViewChild('tooltip') tooltip;

  @ComputedFrom('type', 'organizationContactData', '_rejectStepData')
  get typeConfig(): TypeConfig {
    return this.generateTypeConfig();
  }

  @ComputedFrom('type')
  get isAuditRejected(): boolean {
    return [REJECT_HINT_TYPES.AUDIT_PAUSE, REJECT_HINT_TYPES.AUDIT_REJECT].includes(this.type);
  }

  @ComputedFrom('showTooltip', 'isAuditRejected', '_notes', 'onlyIcon')
  get isInteractive(): boolean {
    return !this.onlyIcon && (!this.showTooltip && this.isAuditRejected ? !!_size(this._notes) : !this.showTooltip);
  }

  constructor(
    private _certificatesService: CertificatesService,
    private _step: StepService,
    private smartModalService: SmartModalService,
    private messageQueryService: MessageQueryService,
  ) {}

  ngOnDestroy() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.notes) {
      this._hasDefaultNotes = !!this.notes;
      this._notes = this.notes;
    }
    if (changes.rejectStepData) {
      this._rejectStepData = this.rejectStepData;
      if (!this._hasDefaultNotes) {
        this._notes = null;
      }
    }
  }

  onHintMoseOver() {
    if (this.showTooltip) {
      this.processTooltip();
    }
  }

  onHintMouseLeave() {
    if (this.showTooltip) {
      this.tooltip.close();
    }
  }

  onHintClick(event) {
    if (this.isInteractive) {
      this.processNotesDialog();
    }

    event.stopPropagation();
  }

  private get stepVerifierLabel() {
    let result = '';
    if (!this._rejectStepData) {
      result = 'VERIFIER';
    } else {
      const { rejectedByRoleId, supervisorLabel, supervisorApprovalTypeId, isSecondarySupervisor } =
        this._rejectStepData;
      switch (rejectedByRoleId) {
        case ROLE_CODES.SUPERVISOR:
          if (supervisorApprovalTypeId === SUPERVISOR_APPROVAL_TYPE.SECONDARY_APPROVAL) {
            result = isSecondarySupervisor ? 'Secondary ' : 'Primary ';
          }
          result += supervisorLabel || defaultSupervisorTitle;
          break;
        case ROLE_CODES.ORGANIZATION_ADMIN:
        default:
          result = 'VERIFIER';
      }
    }
    return result.toUpperCase();
  }

  private generateTypeConfig(): TypeConfig {
    const valueHandleMap = {
      [REJECT_HINT_TYPES.CERTIFICATE]: {
        label: 'REJECTED BY <br />VERIFIER',
        title: 'Credential was rejected',
      },
      [REJECT_HINT_TYPES.STEP]: {
        label: `REJECTED BY <br />${this.stepVerifierLabel}`,
        title: 'Step was rejected',
      },
      [REJECT_HINT_TYPES.AUDIT_PAUSE]: {
        label: 'CREDENTIAL <br />PAUSED',
        title: 'Credential Paused',
      },
      [REJECT_HINT_TYPES.AUDIT_REJECT]: {
        label: 'CORRECTIONS <br />REQUESTED',
        title: 'Corrections Requested',
        confirmButtonText: 'Resubmit Evidence',
      },
      [REJECT_HINT_TYPES.OPTIONAL_PHASE]: {
        label: 'OPTIONAL <br />PHASE',
        title: 'Optional phase',
      },
    };

    return {
      confirmButtonText: '',
      organizationContactData: this.organizationContactData,
      ...valueHandleMap[this.type],
    };
  }

  private getComments(): Observable<any> {
    switch (this.type) {
      case REJECT_HINT_TYPES.STEP:
        return this._step.getRejectComments(this.id);
      case REJECT_HINT_TYPES.CERTIFICATE:
        return this._certificatesService.getRejectComments(this.id);
    }
  }

  private generateFullNotes(notes: string[]) {
    if (!_size(notes)) {
      return notes;
    }
    return notes.map((item: any) => {
      if (typeof item === 'string') {
        return item;
      }
      const rcd = item.rcd ? new DateFormatPipe().transform(item.rcd, 'dateTime') : '';
      return item.note.replace('{rcd}', rcd);
    });
  }

  private processNotesDialog() {
    if (this._notes) {
      this.openNotesPopup();
      return;
    }

    this.getComments().subscribe(
      (data) => {
        this._notes = this.generateFullNotes(data);
        this.openNotesPopup();
      },
      () => this.messageQueryService.addErrorMessage('Please try again later'),
    );
  }

  private openNotesPopup() {
    if (!_size(this._notes)) {
      return;
    }

    this.smartModalService
      .open(RejectedHintModalComponent, {
        notes: this._notes,
        type: this.type,
        config: this.typeConfig,
      })
      .pipe(untilDestroyed(this), skipModalClose())
      .subscribe(() => {
        this.modalConfirm.emit();
      });
  }

  private processTooltip() {
    if (this._notes) {
      this.tooltip.open();
    } else {
      this.getComments().subscribe((data) => {
        this._notes = data;
        this.tooltip.open();
      });
    }
  }
}
